/**
 * One delegated tooltip for the document, including portals and disabled controls.
 * data-tooltip="" uses aria-label; a nonempty value supplies a concise action label.
 * Prefer a few words; put detailed help in accessible descriptions or the UI.
 * Keep this function self-contained: built-in presentations serialize it too.
 */
export function installTooltips() {
  let hovered: HTMLElement | null = null, focused: HTMLElement | null = null;
  let active: HTMLElement | null = null, dismissed: HTMLElement | null = null;
  let bubble: HTMLDivElement | null = null, overBubble = false, pressing = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const id = `aq-tooltip-${crypto.randomUUID()}`;
  const label = (element: HTMLElement) => element.getAttribute("data-tooltip") || element.getAttribute("aria-label") || "";
  const trigger = (target: EventTarget | null) => target instanceof Element ? target.closest<HTMLElement>("[data-tooltip]") : null;
  const visible = (element: HTMLElement) => element.isConnected && !element.closest('[hidden],[inert],[aria-hidden="true"]') && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== "hidden";
  function cancelTimer() { clearTimeout(timer); timer = undefined; }
  function hide() {
    cancelTimer();
    observer.disconnect();
    attributes.disconnect();
    if (active) {
      const descriptions = (active.getAttribute("aria-describedby") || "").split(/\s+/).filter(value => value && value !== id);
      if (descriptions.length) active.setAttribute("aria-describedby", descriptions.join(" "));
      else active.removeAttribute("aria-describedby");
    }
    bubble?.remove(); bubble = null; active = null; overBubble = false;
  }
  function position() {
    if (!active || !bubble) return;
    const rect = active.getBoundingClientRect(), gap = 8;
    const viewport = window.visualViewport;
    const left = viewport?.offsetLeft ?? 0, top = viewport?.offsetTop ?? 0;
    const width = viewport?.width ?? window.innerWidth, height = viewport?.height ?? window.innerHeight;
    bubble.style.maxWidth = `${Math.max(0, Math.min(320, width - gap * 2))}px`;
    const size = bubble.getBoundingClientRect();
    bubble.style.left = `${Math.max(left + gap, Math.min(rect.left + (rect.width - size.width) / 2, left + width - size.width - gap))}px`;
    const above = rect.top - size.height - gap;
    bubble.style.top = `${Math.max(top + gap, Math.min(above >= top + gap ? above : rect.bottom + gap, top + height - size.height - gap))}px`;
  }
  function refresh() {
    if (!active || !bubble) return;
    if (!visible(active) || !active.hasAttribute("data-tooltip") || !label(active)) { hide(); return; }
    if (bubble.textContent !== label(active)) bubble.textContent = label(active);
    position();
  }
  const observer = new MutationObserver(refresh);
  const attributes = new MutationObserver(refresh);
  function show(element: HTMLElement) {
    cancelTimer();
    if (element === dismissed || !visible(element) || !label(element)) return;
    if (active === element) return;
    hide(); active = element;
    bubble = document.createElement("div");
    bubble.id = id; bubble.className = "ui-tooltip"; bubble.role = "tooltip";
    bubble.textContent = label(element);
    // Modal dialogs and fullscreen elements are above the document's stacking layers.
    (element.closest("dialog[open]") || document.fullscreenElement || document.body).append(bubble);
    const descriptions = (element.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
    element.setAttribute("aria-describedby", [...new Set([...descriptions, id])].join(" "));
    position();
    observer.observe(document.body, { childList: true, subtree: true });
    for (let node: HTMLElement | null = element; node; node = node.parentElement) {
      attributes.observe(node, { attributes: true,
        attributeFilter: ["data-tooltip", "aria-label", "hidden", "inert", "aria-hidden", "class", "style", "open"] });
    }
  }
  function update(delay = 0) {
    cancelTimer();
    if (overBubble) return;
    const next = hovered || focused;
    if (next === active) return;
    if (delay) timer = setTimeout(() => { timer = undefined; update(); }, delay);
    else if (next && next !== dismissed) show(next);
    else hide();
  }
  function pointerOver(event: PointerEvent) {
    if (event.pointerType === "touch" || pressing || event.buttons) return;
    if (bubble?.contains(event.target as Node)) { overBubble = true; cancelTimer(); return; }
    const next = trigger(event.target);
    if (next === hovered) return;
    hovered = next; dismissed = null; overBubble = false;
    update(next ? 400 : 150);
  }
  function pointerOut(event: PointerEvent) {
    if (event.pointerType === "touch") return;
    if (bubble?.contains(event.relatedTarget as Node)) { hovered = null; overBubble = true; cancelTimer(); return; }
    if (trigger(event.relatedTarget) === hovered && !overBubble) return;
    hovered = trigger(event.relatedTarget); overBubble = false;
    if (!hovered && !focused) dismissed = null;
    update(150); // Allow crossing the gap to read the tooltip itself.
  }
  function focusIn(event: FocusEvent) {
    focused = trigger(event.target);
    if (pressing) { dismissed = focused; return; }
    if (focused) { dismissed = null; show(focused); } else update();
  }
  function focusOut(event: FocusEvent) {
    focused = trigger(event.relatedTarget);
    update();
  }
  function dismiss() { dismissed = active || hovered || focused; hide(); }
  function pointerDown() { pressing = true; dismiss(); }
  function pointerUp() { pressing = false; }
  function keyDown(event: KeyboardEvent) {
    if (event.key !== "Escape" || event.isComposing || !bubble) return;
    dismiss();
    // Recording keeps its existing Escape-to-stop shortcut.
    if (document.querySelector('[data-dictating="true"]')) return;
    event.preventDefault(); event.stopImmediatePropagation();
  }
  function reset() { hovered = null; focused = null; dismissed = null; pressing = false; hide(); }
  document.addEventListener("pointerover", pointerOver, true);
  document.addEventListener("pointerout", pointerOut, true);
  document.addEventListener("focusin", focusIn, true);
  document.addEventListener("focusout", focusOut, true);
  document.addEventListener("pointerdown", pointerDown, true);
  document.addEventListener("pointerup", pointerUp, true);
  document.addEventListener("pointercancel", pointerUp, true);
  document.addEventListener("click", dismiss, true);
  document.addEventListener("keydown", keyDown, true);
  document.addEventListener("scroll", dismiss, true);
  document.addEventListener("fullscreenchange", reset);
  document.addEventListener("visibilitychange", reset);
  window.addEventListener("resize", dismiss);
  window.addEventListener("blur", reset);
  window.visualViewport?.addEventListener("resize", dismiss);
  window.visualViewport?.addEventListener("scroll", dismiss);
  return () => {
    reset();
    document.removeEventListener("pointerover", pointerOver, true);
    document.removeEventListener("pointerout", pointerOut, true);
    document.removeEventListener("focusin", focusIn, true);
    document.removeEventListener("focusout", focusOut, true);
    document.removeEventListener("pointerdown", pointerDown, true);
    document.removeEventListener("pointerup", pointerUp, true);
    document.removeEventListener("pointercancel", pointerUp, true);
    document.removeEventListener("click", dismiss, true);
    document.removeEventListener("keydown", keyDown, true);
    document.removeEventListener("scroll", dismiss, true);
    document.removeEventListener("fullscreenchange", reset);
    document.removeEventListener("visibilitychange", reset);
    window.removeEventListener("resize", dismiss);
    window.removeEventListener("blur", reset);
    window.visualViewport?.removeEventListener("resize", dismiss);
    window.visualViewport?.removeEventListener("scroll", dismiss);
  };
}
